import { useEffect, useState } from 'react';
import {
  CampaignsGetResponse,
  CampaignsGetResponse_Campaign,
  CampaignsService,
  CGenre,
  CRegion,
  GenresService,
  RegionsService
} from '../../../apis/api-client';
import FiestaInfiniteSpinner from 'src/shared/infinite-spinner/FiestaInfiniteSpinner';
import CampaignCard from './components/CampaignCard';
import { t } from 'i18next';
import { tt } from 'src/shared';
import OrganiserStripComponent from './OrganiserStripComponent';
import { useRevealOnScroll } from 'src/shared';

export default function CampaignsComponent() {
  const headRef = useRevealOnScroll<HTMLDivElement>();
  const barRef = useRevealOnScroll<HTMLDivElement>();

  const [campaigns, setCampaigns] = useState<CampaignsGetResponse_Campaign[]>([]);
  const [genres, setGenres] = useState<CGenre[]>([]);
  const [selectedGenre, setSelectedGenre] = useState<string | undefined>(undefined);

  const take = 12;
  const [autoUpdate, setAutoUpdate] = useState(false);
  const [more, setMore] = useState<boolean>(true);
  const [searchValue, setSearchValue] = useState<string>('');
  const [regionValue, setRegionValue] = useState<CRegion>(CRegion.EUROPE);
  const [availableRegions, setAvailableRegions] = useState<CRegion[]>([CRegion.EUROPE, CRegion.NORTH_AMERICA]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      const [regionResponse, genreResponse] = await Promise.all([
        RegionsService.get(true),
        GenresService.get(undefined, 0, 100).catch(() => null),
      ]);

      if (regionResponse?.availableRegions) {
        setAvailableRegions(regionResponse.availableRegions);
        if (regionResponse.suggested) setRegionValue(regionResponse.suggested);
      }
      if (genreResponse?.result) {
        setGenres(genreResponse.result);
      }
    })();
  }, []);

  useEffect(() => {
    if (!autoUpdate) return;
    (async () => {
      if (searchValue && searchValue.length < 2) return;
      const response = await loadCampaigns(0, take, searchValue || undefined, regionValue);
      if (response) {
        setCampaigns(response.result!);
        setMore(response.result!.length < response.totalCount!);
      }
    })();
  }, [searchValue, regionValue]);

  useEffect(() => {
    (async () => {
      let search: string | undefined;
      let region: CRegion | undefined;

      const searchParams = new URLSearchParams(window.location.search);
      const searchTemp = searchParams.get('s');
      if (searchTemp) { setSearchValue(searchTemp); search = searchTemp; }
      const regionTemp = searchParams.get('r');
      if (regionTemp && (regionTemp as CRegion)) { setRegionValue(regionTemp as CRegion); region = regionTemp as CRegion; }

      const response = await loadCampaigns(0, take, search, region);
      if (response) {
        setCampaigns(response.result!);
        setMore(response.result!.length < response.totalCount!);
        if (searchTemp || regionTemp) window.location.href = '#events';
      }
      setAutoUpdate(true);
    })();
  }, []);

  async function loadCampaigns(
    skip: number,
    take: number,
    searchTerm?: string,
    region?: CRegion,
  ): Promise<CampaignsGetResponse | undefined> {
    if (loading) return;
    setLoading(true);
    try {
      const noCache = new URLSearchParams(window.location.search).get('noCache') === 'true';
      const response = await CampaignsService.getLandingpage(skip, take, noCache, undefined, searchTerm, region);
      if (response?.result) return response;
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  const filteredCampaigns = selectedGenre
    ? campaigns.filter(c => c.event?.genres?.some(g => g.id === selectedGenre))
    : campaigns;

  return (
    <section className="section-pad" id="events" aria-labelledby="jp-dir-title">
      <div className="wrap">
        <div ref={headRef} className="sec-head reveal" style={{ textAlign: 'center' }}>
          <span className="eyebrow" style={{ justifyContent: 'center' }}>
            <span className="dot" />{t('joinPage.v2.directory.label')}
          </span>
          <h2 className="display" id="jp-dir-title">{t('joinPage.v2.directory.title')}</h2>
        </div>

        <div ref={barRef} className="events__bar reveal">
          <label className="search">
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="9" cy="9" r="6" />
              <path d="m16 16-3.5-3.5" />
            </svg>
            <input
              id="q"
              type="search"
              placeholder={t('joinPage.v2.directory.searchPlaceholder')}
              value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
              autoComplete="off"
            />
          </label>

          {genres.length > 0 && (
            <div className="chips" id="chips">
              <button
                className="chip"
                aria-pressed={!selectedGenre ? true : false}
                onClick={() => setSelectedGenre(undefined)}
              >
                {t('joinPage.v2.directory.allGenres')}
              </button>
              {genres.map(g => (
                <button
                  key={g.id}
                  className="chip"
                  aria-pressed={selectedGenre === g.id ? true : false}
                  onClick={() => setSelectedGenre(selectedGenre === g.id ? undefined : g.id)}
                  disabled={loading}
                >
                  {tt(g.id!, 'genre_title', g.title!)}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="event-grid" id="grid">
          {filteredCampaigns.map((campaign, i) => (
            <CampaignCard key={campaign.program?.id ?? i} campaign={campaign} elevated={campaign.event?.elevated ?? false} />
          ))}
        </div>

        {loading && <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}><FiestaInfiniteSpinner /></div>}

        {!loading && filteredCampaigns.length === 0 && (
          <div className="events__empty show" id="empty">
            {t('joinPage.noResultsMessage')}
          </div>
        )}

        {more && !selectedGenre && !loading && (
          <div className="load-more">
            <button
              className="btn btn--ghost"
              onClick={async () => {
                const response = await loadCampaigns(campaigns.length, take, searchValue || undefined, regionValue);
                if (response) {
                  setMore(response.result!.length + campaigns.length < response.totalCount!);
                  setCampaigns(prev => [...prev, ...response.result!]);
                }
              }}
            >
              {t('joinPage.search.loadMore')}
            </button>
          </div>
        )}

        <OrganiserStripComponent />
      </div>
    </section>
  );
}
